/* Loaded globally by snippets/studio-cart-guard.liquid, including native carts.
 * Checkout validation remains a separate Shopify server-side responsibility.
 */
(() => {
  const config = document.getElementById('studio-cart-guard');
  if (!config || window.dpsCartGuardLoaded) return;
  window.dpsCartGuardLoaded = true;
  const variantId = config.dataset.expressVariant;
  const base = config.dataset.root;
  if (!variantId || !base?.startsWith('/') || base.startsWith('//') || /[?#\\]/.test(base)) return;
  let pending;
  let checkingOut = false;
  let lastKnownCart;
  const hasStudio = (cart) =>
    !Array.isArray(cart?.items) ||
    cart.items.some((line) => isUpgrade(line) || line.properties?._dps_selection);

  const isUpgrade = (line) => String(line.variant_id) === variantId;
  // One delivery line at checkout (Pierrick, 2026-10-16): Express is carried by
  // the hidden _dps_delivery property and charged by the Studio carrier rate.
  // Any Express cart line from an earlier model is removed, and the visible
  // « Regular shipping » property is stripped from portraits.
  const labelled = (line) =>
    line.properties?._dps_selection &&
    Object.prototype.hasOwnProperty.call(line.properties, 'Regular shipping');
  const plan = (cart) => {
    if (!Array.isArray(cart?.items)) throw new Error('Invalid cart response');
    return { remove: cart.items.filter(isUpgrade).map((line) => line.key) };
  };

  function message(text) {
    let notice = document.getElementById('studio-cart-guard-notice');
    if (!notice) {
      notice = document.createElement('div');
      notice.id = 'studio-cart-guard-notice';
      notice.setAttribute('role', 'alert');
      notice.style.cssText =
        'position:fixed;bottom:16px;left:16px;right:16px;z-index:2147483647;padding:16px;background:#fff;color:#222;border:1px solid #888;box-shadow:0 2px 12px #0002;text-align:center';
      document.body.append(notice);
    }
    notice.textContent = text;
  }

  function reconcile() {
    if (pending) return pending;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 15000);
    const request = async (path, options = {}) => {
      const response = await fetch(`${base}${path}`, {
        ...options,
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Cart update unavailable');
      return response.json();
    };
    const run = async () => {
      // Re-read inside the same lock as Studio handoff; event payloads can be stale.
      let cart = await request('cart.js');
      if (Array.isArray(cart?.items)) lastKnownCart = cart;
      const action = plan(cart);
      let changed = false;
      if (action.remove.length) {
        await request('cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: Object.fromEntries(action.remove.map((key) => [key, 0])),
          }),
        });
        const confirmed = await request('cart.js');
        if (plan(confirmed).remove.length) throw new Error('Delivery update not confirmed');
        cart = confirmed;
        changed = true;
      }
      // Preserve all signed selection properties when removing the label.
      for (const line of cart.items.filter(labelled)) {
        const properties = { ...line.properties };
        delete properties['Regular shipping'];
        cart = await request('cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: line.key, quantity: line.quantity, properties }),
        });
        if (
          !cart.items?.some(
            (item) =>
              item.properties?._dps_selection === line.properties._dps_selection && !labelled(item),
          )
        )
          throw new Error('Shipping label update not confirmed');
        changed = true;
      }
      lastKnownCart = cart;
      return changed;
    };
    pending = (
      navigator.locks
        ? navigator.locks.request('dps-shopify-cart', { signal: controller.signal }, run)
        : run()
    ).finally(() => {
      clearTimeout(timer);
      pending = null;
    });
    return pending;
  }

  async function cleanNativeCart(cart) {
    if (Array.isArray(cart?.items)) lastKnownCart = cart;
    try {
      if (
        !Array.isArray(cart?.items) ||
        (!cart.items.some(isUpgrade) && !cart.items.some(labelled))
      )
        return;
      const removed = await reconcile();
      // Refresh both drawer and full cart from Shopify, including totals and badges.
      if (removed && !checkingOut) location.reload();
    } catch {
      message('We could not check delivery. Please retry checkout; your portrait is saved.');
    }
  }
  document.addEventListener('cart:updated', async (event) => {
    // An edit arriving during an earlier check needs its own fresh read.
    if (pending) await pending.catch(() => {});
    cleanNativeCart(event.detail?.cart);
  });

  async function checkout(event, destination) {
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkingOut) return;
    checkingOut = true;
    message('Checking your cart…');
    try {
      await reconcile();
      location.assign(destination);
    } catch {
      // An unrelated cart must not depend on the Studio service guard. Unknown
      // or Studio carts still fail closed when delivery cannot be verified.
      if (!hasStudio(lastKnownCart)) {
        location.assign(destination);
        return;
      }
      checkingOut = false;
      message('We could not check delivery. Please retry checkout; your portrait is saved.');
    }
  }
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const anchor = target?.closest('a[href]');
      if (
        !anchor ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      )
        return;
      const url = new URL(anchor.href, location.href);
      if (url.origin === location.origin && /\/checkout\/?$/.test(url.pathname))
        checkout(event, url.href);
    },
    true,
  );
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      const url = new URL(form.action, location.href);
      if (url.origin !== location.origin) return;
      if (
        /\/checkout\/?$/.test(url.pathname) ||
        (/\/cart\/?$/.test(url.pathname) && event.submitter?.name === 'checkout')
      ) {
        checkout(event, `${base}checkout`);
      }
    },
    true,
  );

  // Liquid supplies the initial cart: ordinary page views need no cart API request.
  try {
    cleanNativeCart(JSON.parse(config.textContent));
  } catch {
    /* Checkout still performs a fresh, mandatory cart check. */
  }
})();
